<template>
  <page class="report-page">
    <router-link class="breadcrumb" :to="{ name: routes.admin.adminDashboard }">
      <span class="breadcrumb-icon">←</span>
      {{ $adminT('adminAll.back_to_operations_console_6f9440', 'Back to Operations Console') }}
    </router-link>
    <div class="report-header">
      <h1>{{ $adminT('adminAll.mjesecni_izvjestaj_coreevent_prihoda_2ec8d7', 'Mjesečni izvještaj CoreEvent prihoda') }}</h1>
      <h2 v-if="!isLoading">{{ $adminT('adminAll.period_od_946a13', 'Period od') }} {{ formatDate(from) }} {{ $adminT('adminAll.do_eadcd9', 'do') }} {{ formatDate(to) }}</h2>
      <div v-else class="report-period-skeleton" />
    </div>

    <!-- CoreEvent ticket fee events -->
    <h3>{{ $adminT('adminAll.naknade_za_izdavanje_ulaznica_feb6de', 'Naknade Za Izdavanje Ulaznica') }}</h3>
    <div class="report-table-wrapper">
      <table class="report-table" :class="{ 'report-table--loading': isLoading }">
        <thead>
          <tr>
            <th>{{ $adminT('adminAll.doga_aj_161b6b', 'Događaj') }}</th>
            <th>{{ $adminT('adminAll.organizator_86301b', 'Organizator') }}</th>
            <th>{{ $adminT('adminAll.prosj_naknada_03d5de', 'Prosj. Naknada (€)') }}</th>
            <th>{{ $adminT('adminAll.kolicina_2624ed', 'Količina') }}</th>
            <th>{{ $adminT('adminAll.ukupno_naknada_057c2c', 'Ukupno Naknada (€)') }}</th>
            <th>{{ $adminT('adminAll.pdv_na_naknadu_b7b505', 'PDV na Naknadu (€)') }}</th>
          </tr>
        </thead>
        <tbody v-if="isLoading">
          <tr v-for="row in skeletonRows" :key="`fixed-skeleton-${row}`">
            <td v-for="column in 6" :key="`fixed-skeleton-${row}-${column}`">
              <div class="table-skeleton-line" />
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr v-for="item in reportData" :key="getReportRowKey(item)">
            <td :data-label="$adminT('adminAll.doga_aj_161b6b', 'Događaj')">{{ item.eventTitle }}</td>
            <td :data-label="$adminT('adminAll.organizator_86301b', 'Organizator')">{{ item.organization }}</td>
            <td :data-label="$adminT('adminAll.prosj_naknada_03d5de', 'Prosj. Naknada (€)')">
              {{
                formatCurrency(
                  item.totalCoreEventEarningsWithoutVat / item.coreEventFeeTicketCount || 0,
                )
              }}
            </td>
            <td :data-label="$adminT('adminAll.kolicina_2624ed', 'Količina')">{{ getCoreEventFeeTicketCount(item) }}</td>
            <td :data-label="$adminT('adminAll.ukupno_naknada_057c2c', 'Ukupno Naknada (€)')">
              {{ formatCurrency(item.totalCoreEventEarningsWithoutVat) }}
            </td>
            <td :data-label="$adminT('adminAll.pdv_na_naknadu_b7b505', 'PDV na Naknadu (€)')">
              {{ formatCurrency(item.totalCoreEventEarningsWithoutVat * 0.25) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Other Events -->
    <h3 v-if="!fixedFeeOnly">{{ $adminT('adminAll.ostale_naknade_a1656d', 'Ostale Naknade') }}</h3>
    <div v-if="!fixedFeeOnly" class="report-table-wrapper">
      <table class="report-table" :class="{ 'report-table--loading': isLoading }">
        <thead>
          <tr>
            <th>{{ $adminT('adminAll.doga_aj_161b6b', 'Događaj') }}</th>
            <th>{{ $adminT('adminAll.organizator_86301b', 'Organizator') }}</th>
            <th>{{ $adminT('adminAll.kolicina_2624ed', 'Količina') }}</th>
            <th>{{ $adminT('adminAll.ukupno_prodano_e396b5', 'Ukupno Prodano (€)') }}</th>
            <th>{{ $adminT('adminAll.coreevent_naknada_79f027', 'CoreEvent Naknada (€)') }}</th>
            <th>{{ $adminT('adminAll.coreevent_naknada_s_pdv_om_40b370', 'CoreEvent Naknada s PDV-om (€)') }}</th>
          </tr>
        </thead>
        <tbody v-if="isLoading">
          <tr v-for="row in skeletonRows" :key="`purchase-skeleton-${row}`">
            <td v-for="column in 6" :key="`purchase-skeleton-${row}-${column}`">
              <div class="table-skeleton-line" />
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr v-for="summary in purchaseSummaries" :key="summary.event.id">
            <td :data-label="$adminT('adminAll.doga_aj_161b6b', 'Događaj')">{{ summary.event.title }}</td>
            <td :data-label="$adminT('adminAll.organizator_86301b', 'Organizator')">{{ summary.event.organization.legalName }}</td>
            <td :data-label="$adminT('adminAll.kolicina_2624ed', 'Količina')">{{ summary.totalSoldCount }}</td>
            <td :data-label="$adminT('adminAll.ukupno_prodano_e396b5', 'Ukupno Prodano (€)')">{{ formatCurrency(summary.totalSoldAmount) }}</td>
            <td :data-label="$adminT('adminAll.coreevent_naknada_79f027', 'CoreEvent Naknada (€)')">
              {{ formatCurrency(summary.totalCoreEventFee) }}
            </td>
            <td :data-label="$adminT('adminAll.coreevent_naknada_s_pdv_om_40b370', 'CoreEvent Naknada s PDV-om (€)')">
              {{ formatCurrency(summary.totalCoreEventFeeWithVAT) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Totals -->
    <div class="totals">
      <h3 class="total-row">
        <span>{{ $adminT('adminAll.ukupno_naknade_za_izdavanje_ulaznica_dba6a2', 'Ukupno Naknade Za Izdavanje Ulaznica:') }}</span>
        <span>{{ formatCurrency(totalCoreEventTicketFeeEarnings) }} €</span>
      </h3>
      <h3 v-if="!fixedFeeOnly" class="total-row">
        <span>{{ $adminT('adminAll.ukupno_ostale_naknade_18fa44', 'Ukupno Ostale Naknade:') }}</span>
        <span>{{ formatCurrency(totalCoreEventEarnings - totalCoreEventTicketFeeEarnings) }} €</span>
      </h3>
      <h3 v-if="!fixedFeeOnly" class="total-row">
        <span>{{ $adminT('adminAll.ukupno_naknada_776c8f', 'Ukupno Naknada:') }}</span>
        <span>{{ formatCurrency(totalCoreEventEarnings) }} €</span>
      </h3>
      <h3 class="total-row">
        <span>{{ $adminT('adminAll.ukupno_pdv_na_naknadu_7023c9', 'Ukupno PDV na Naknadu:') }}</span>
        <span>{{ formatCurrency(totalCoreEventEarnings * 0.25) }} €</span>
      </h3>
      <hr>
      <h3 class="total-row total-row--strong">
        <span>{{ $adminT('adminAll.ukupno_naknada_s_pdv_om_8845ff', 'Ukupno Naknada s PDV-om:') }}</span>
        <span>{{ formatCurrency(totalCoreEventEarnings * 1.25) }} €</span>
      </h3>
    </div>
  </page>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import moment from 'moment'
import gql from 'graphql-tag'

const PRINT_SCROLL_SELECTOR = '.report-table-wrapper'

export default {
  mixins: [AdminAllMessages],
  provide: { adminLocalization: true },
  props: {
    year: String,
    month: String,
    fixedFeeOnly: String,
  },
  data() {
    return {
      from: null,
      to: null,
      reportData: [],
      purchaseSummaries: [],
      totalCoreEventTicketFeeEarnings: 0,
      totalCoreEventEarnings: 0,
      refundInfo: null,
      printScrollPositions: [],
      beforePrintHandler: null,
      afterPrintHandler: null,
      printMediaQuery: null,
      printMediaQueryHandler: null,
    }
  },
  computed: {
    isLoading() {
      return !!(
        this.$apollo &&
        this.$apollo.queries &&
        this.$apollo.queries.getMonthlyReport &&
        this.$apollo.queries.getMonthlyReport.loading
      )
    },
    skeletonRows() {
      return 6
    },
  },
  mounted() {
    if (typeof document !== 'undefined') {
      document.body.classList.add('admin-report-print-layout')
    }
    this.setupPrintHandlers()
  },
  beforeDestroy() {
    this.cleanupPrintHandlers()
    if (typeof document !== 'undefined') {
      document.body.classList.remove('admin-report-print-layout')
    }
  },
  methods: {
    formatDate(date) {
      return moment.utc(date).format('DD. MM. YYYY HH:mm')
    },
    formatCurrency(value) {
      return parseFloat(value).toFixed(2)
    },
    getCoreEventFeeTicketCount(item) {
      return item.coreEventFeeTicketCount === undefined || item.coreEventFeeTicketCount === null
        ? item.ticketCount
        : item.coreEventFeeTicketCount
    },
    getReportRowKey(item) {
      return (
        item.reportRowKey ||
        `${item.eventId}:${item.isCoreEventFeeRefundAdjustment ? 'refund-adjustment' : 'sale'}`
      )
    },
    getPrintScrollContainers() {
      if (typeof document === 'undefined') {
        return []
      }

      return Array.from(document.querySelectorAll(PRINT_SCROLL_SELECTOR))
    },
    preparePrintLayout() {
      this.printScrollPositions = this.getPrintScrollContainers().map(container => {
        const position = {
          container,
          top: container.scrollTop,
          left: container.scrollLeft,
        }

        container.scrollTop = 0
        container.scrollLeft = 0

        return position
      })
    },
    restorePrintLayout() {
      this.printScrollPositions.forEach(({ container, top, left }) => {
        if (!container || !container.isConnected) {
          return
        }

        container.scrollTop = top
        container.scrollLeft = left
      })
      this.printScrollPositions = []
    },
    setupPrintHandlers() {
      if (typeof window === 'undefined') {
        return
      }

      this.beforePrintHandler = () => this.preparePrintLayout()
      this.afterPrintHandler = () => this.restorePrintLayout()
      window.addEventListener('beforeprint', this.beforePrintHandler)
      window.addEventListener('afterprint', this.afterPrintHandler)

      if (typeof window.matchMedia !== 'function') {
        return
      }

      this.printMediaQuery = window.matchMedia('print')
      this.printMediaQueryHandler = event => {
        if (event.matches) {
          this.preparePrintLayout()
        } else {
          this.restorePrintLayout()
        }
      }

      if (typeof this.printMediaQuery.addEventListener === 'function') {
        this.printMediaQuery.addEventListener('change', this.printMediaQueryHandler)
      } else if (typeof this.printMediaQuery.addListener === 'function') {
        this.printMediaQuery.addListener(this.printMediaQueryHandler)
      }
    },
    cleanupPrintHandlers() {
      if (typeof window !== 'undefined') {
        if (this.beforePrintHandler) {
          window.removeEventListener('beforeprint', this.beforePrintHandler)
        }
        if (this.afterPrintHandler) {
          window.removeEventListener('afterprint', this.afterPrintHandler)
        }
      }

      if (this.printMediaQuery && this.printMediaQueryHandler) {
        if (typeof this.printMediaQuery.removeEventListener === 'function') {
          this.printMediaQuery.removeEventListener('change', this.printMediaQueryHandler)
        } else if (typeof this.printMediaQuery.removeListener === 'function') {
          this.printMediaQuery.removeListener(this.printMediaQueryHandler)
        }
      }

      this.restorePrintLayout()
      this.beforePrintHandler = null
      this.afterPrintHandler = null
      this.printMediaQuery = null
      this.printMediaQueryHandler = null
    },
  },
  apollo: {
    getMonthlyReport: {
      query: gql`
        query GetMonthlyReport($year: Int!, $month: Int!, $fixedFeeOnly: Boolean) {
          getCoreEventMonthlyReport(year: $year, month: $month, fixedFeeOnly: $fixedFeeOnly)
        }
      `,
      variables() {
        return {
          year: this.year ? parseInt(this.year) : new Date().getFullYear(),
          month: this.month ? parseInt(this.month) : new Date().getMonth() + 1,
          fixedFeeOnly: !!this.fixedFeeOnly,
        }
      },
      result({ data }) {
        if (data && data.getCoreEventMonthlyReport) {
          const report = data.getCoreEventMonthlyReport
          this.from = report.from
          this.to = report.to
          this.reportData = report.reportData
            .filter(r => !!r.totalCoreEventEarningsWithoutVat)
            .sort((a, b) => b.totalCoreEventEarningsWithoutVat - a.totalCoreEventEarningsWithoutVat)
          this.purchaseSummaries = (report.purchaseSummaries || [])
            .filter(r => !!r.totalCoreEventFeeWithVAT)
            .sort((a, b) => b.totalCoreEventFeeWithVAT - a.totalCoreEventFeeWithVAT)
          this.totalCoreEventTicketFeeEarnings = Number(
            report.totalCoreEventTicketFeeEarnings != null
              ? report.totalCoreEventTicketFeeEarnings
              : report.totalCoreEventFixedFeeEarnings || 0,
          )
          this.totalCoreEventEarnings = report.totalCoreEventEarnings
          this.refundInfo = report.refundInfo
        }
      },
    },
  },
}
</script>

<style scoped>
.breadcrumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  color: #2563eb;
  font-weight: 700;
  padding: 0;
  margin: 0 0 12px;
  text-decoration: none;
}
.breadcrumb-icon {
  font-size: 16px;
}
.report-page {
  background: white;
  max-width: 1200px;
  margin: auto;
  display: block;
  padding: 24px;
  box-sizing: border-box;
}

.report-header {
  margin-bottom: 24px;
}

.report-header h1 {
  margin: 0 0 8px;
}

.report-header h2 {
  margin: 0;
}

.report-period-skeleton {
  width: min(100%, 360px);
  height: 28px;
  border-radius: 6px;
  background: linear-gradient(90deg, #eef2f7 25%, #e2e8f0 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: table-skeleton-shimmer 1.4s ease-in-out infinite;
}

.report-header h1,
.report-header h2,
.report-page h3 {
  overflow-wrap: anywhere;
}

.report-page h3 {
  margin: 0 0 12px;
}

@media print {
  body,
  page[size='A4'] {
    margin: 0;
    box-shadow: 0;
  }

  .breadcrumb {
    display: none !important;
  }

  .report-table-wrapper {
    max-height: none !important;
    height: auto !important;
    overflow: visible !important;
  }

  .report-table thead th {
    position: static;
    box-shadow: none;
  }

  .report-table thead,
  .report-table tfoot {
    display: table-row-group !important;
  }

  .report-table tr,
  .report-table td,
  .report-table th {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
}

.container {
  padding: 60px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.company-info {
  align-self: end;
}

.nowrap {
  white-space: nowrap;
}

.totals {
  margin-top: 24px;
  margin-left: auto;
  width: min(100%, 420px);
}

.totals hr {
  margin: 16px 0;
}

.total-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.total-row--strong {
  font-weight: 700;
}

.total-row > span:last-child {
  text-align: right;
  white-space: nowrap;
}

.report-table-wrapper {
  width: 100%;
  max-height: min(70vh, 640px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 24px;
}

.report-table {
  width: 100%;
  min-width: 760px;
  color: #333;
  background: white;
  border: 1px solid grey;
  font-size: 12pt;
  border-collapse: collapse;
}

.report-table thead th,
.report-table tfoot th {
  color: #777;
  background: #eef2f7;
}

.report-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #eef2f7;
  background-clip: padding-box;
  box-shadow: inset 0 -1px 0 #d1d5db;
}

.report-table caption {
  padding: 0.5em;
}

.report-table th,
.report-table td {
  padding: 0.5em;
  border: 1px solid lightgrey;
  vertical-align: top;
  text-align: left;
}

.report-table--loading td {
  padding-top: 0.75em;
  padding-bottom: 0.75em;
}

.table-skeleton-line {
  width: 100%;
  height: 16px;
  border-radius: 999px;
  background: linear-gradient(90deg, #eef2f7 25%, #e2e8f0 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: table-skeleton-shimmer 1.4s ease-in-out infinite;
}

@keyframes table-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

[data-table-theme*='zebra'] tbody tr:nth-of-type(odd) {
  background: rgba(0, 0, 0, 0.05);
}

[data-table-theme*='zebra'][data-table-theme*='dark'] tbody tr:nth-of-type(odd) {
  background: rgba(255, 255, 255, 0.05);
}

/* Dark Style */
[data-table-theme*='dark'] {
  color: #ddd;
  background: #333;
  font-size: 12pt;
  border-collapse: collapse;
}

[data-table-theme*='dark'] thead th,
[data-table-theme*='dark'] tfoot th {
  color: #aaa;
  background: rgba(0255, 255, 255, 0.15);
}

[data-table-theme*='dark'] caption {
  padding: 0.5em;
}

[data-table-theme*='dark'] th,
[data-table-theme*='dark'] td {
  padding: 0.5em;
  border: 1px solid grey;
}

@media (max-width: 768px) {
  .report-page {
    padding: 14px;
  }

  .breadcrumb {
    margin-bottom: 14px;
    line-height: 1.3;
  }

  .report-header {
    margin-bottom: 18px;
  }

  .report-header h1 {
    margin-bottom: 6px;
    font-size: 24px;
    line-height: 1.2;
  }

  .report-header h2 {
    font-size: 16px;
    line-height: 1.35;
  }

  .report-page h3 {
    margin-bottom: 10px;
    font-size: 18px;
    line-height: 1.3;
  }

  .report-table-wrapper {
    margin-bottom: 18px;
  }

  .totals {
    margin-top: 18px;
    width: 100%;
  }

  .total-row {
    flex-direction: column;
    gap: 2px;
    margin-bottom: 12px;
  }

  .total-row > span:last-child {
    text-align: left;
    white-space: normal;
  }

  .totals hr {
    margin: 12px 0;
  }
}
</style>

<style>
@media print {
  body.admin-report-print-layout .header,
  body.admin-report-print-layout .footer,
  body.admin-report-print-layout .application-toolbar,
  body.admin-report-print-layout .application-toolbar-zkm,
  body.admin-report-print-layout .back-button-zkm {
    display: none !important;
  }
}
</style>
